import { WeaponCharacteristics, WeaponMeta } from "game-common/models"
import { itemDisplayName } from "game-common/item/display_name"

// What a weapon is called on a card, and a few words on what it is good at -- for the role picker's
// Defender card, where the weapon a recruit carries decides what it is worth on the line (four dagger
// defenders lose a siege that four longswords hold).
//
// DERIVED, NOT LISTED. Every word comes from the weapon's own stats in the content's weapon table
// (content/game/entities/profiles.json, live here as WeaponCharacteristics), measured against the
// TYPICAL weapon in that same table -- the median of each stat across it. A retuned weapon, or a new
// one, describes itself without anybody editing this file, and nothing below names a weapon.
//
// One trait, the most decisive first:
//
//   cleaves two     hits more than one enemy a swing                  (maxEntitiesHit)
//   long reach      strikes from further out than the typical weapon  (maxDistance)
//   heavy blows     at least twice the typical weapon's damage        (baseDamage)
//   knocks back     shoves what it hits                               (pushStrength)
//   quick strikes   swings faster than the typical weapon             (firingLatchMs)
//
// and, failing all of those, the honest one: "short reach" when it strikes from closer in than the
// typical weapon. A weapon that is merely typical gets no note at all.

export interface WeaponKit {
    name: string
    note?: string
}

const NUMBER_WORDS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"]

const median = (values: number[]): number | undefined => {
    if (values.length === 0) {
        return undefined
    }
    const sorted = [...values].sort((a, b) => a - b)
    const mid = Math.floor(sorted.length / 2)
    return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

// The typical weapon's value for one stat. Read at call time: the table is rebound when content is
// republished (see bindContent in common/models.ts).
const typical = (stat: keyof WeaponMeta): number | undefined =>
    median(
        Object.values(WeaponCharacteristics || {})
            .map(meta => meta?.[stat])
            .filter((value): value is number => typeof value === "number" && value > 0),
    )

const positive = (value: unknown): value is number => typeof value === "number" && value > 0

// What the weapon is called everywhere else: its item's display name ("iron-pickaxe" -> "Pickaxe, iron").
export const weaponName = (weaponType: string): string => itemDisplayName(weaponType)

export const weaponStrength = (weaponType: string): string | undefined => {
    const meta: WeaponMeta | undefined = (WeaponCharacteristics as Record<string, WeaponMeta>)?.[weaponType]
    if (!meta) {
        return undefined
    }

    const hits = meta.maxEntitiesHit || 1
    if (hits > 1) {
        return `cleaves ${NUMBER_WORDS[hits] || hits}`
    }

    const reach = typical("maxDistance")
    if (positive(meta.maxDistance) && reach !== undefined && meta.maxDistance > reach) {
        return "long reach"
    }

    const damage = typical("baseDamage")
    if (positive(meta.baseDamage) && damage !== undefined && meta.baseDamage >= 2 * damage) {
        return "heavy blows"
    }

    if (positive(meta.pushStrength)) {
        return "knocks back"
    }

    const swing = typical("firingLatchMs")
    if (positive(meta.firingLatchMs) && swing !== undefined && meta.firingLatchMs < swing) {
        return "quick strikes"
    }

    if (positive(meta.maxDistance) && reach !== undefined && meta.maxDistance < reach) {
        return "short reach"
    }
    return undefined
}

export const weaponKit = (weaponType: string): WeaponKit => ({
    name: weaponName(weaponType),
    note: weaponStrength(weaponType),
})
